import React from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Heart, Compass, Sparkle, ChatsCircle, Crown, UserCircle } from "@phosphor-icons/react";
import MatchOnboarding from "@/pages/match/MatchOnboarding";
import MatchDiscovery from "@/pages/match/MatchDiscovery";
import MatchLikesYou from "@/pages/match/MatchLikesYou";
import MatchMatches from "@/pages/match/MatchMatches";
import MatchPlans from "@/pages/match/MatchPlans";
import MatchProfile from "@/pages/match/MatchProfile";

const TABS = [
  { key: "discovery", label: "Discover", icon: Compass },
  { key: "likes", label: "Likes You", icon: Sparkle },
  { key: "matches", label: "Matches", icon: ChatsCircle },
  { key: "plans", label: "Plans", icon: Crown },
  { key: "profile", label: "Profile", icon: UserCircle },
];

export default function Match() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "discovery";
  const setTab = (t) => setParams({ tab: t });

  const { data: me, isLoading, refetch } = useQuery({
    queryKey: ["match-me"],
    queryFn: async () => (await api.get("/match/me")).data,
  });

  if (isLoading) {
    return <div className="mx-auto max-w-2xl py-20 text-center text-muted-foreground" data-testid="match-loading">Loading iYeneh Match…</div>;
  }

  if (!me?.has_profile) {
    return <MatchOnboarding onDone={() => refetch()} />;
  }

  return (
    <div className="mx-auto max-w-4xl" data-testid="match-hub">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-rose-500/15 text-rose-500">
          <Heart size={24} weight="fill" />
        </div>
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">iYeneh Match</h1>
          <p className="text-sm text-muted-foreground">Private, global, and safe — meet people who fit you.</p>
        </div>
      </div>

      <div className="sticky top-[64px] z-20 -mx-4 mb-6 overflow-x-auto border-b border-border bg-background/80 px-4 backdrop-blur md:top-[68px]">
        <div className="flex min-w-max gap-1">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`match-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3.5 py-3 text-sm font-medium transition-colors ${
                tab === t.key ? "border-rose-500 text-rose-500" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              <t.icon size={17} weight={tab === t.key ? "fill" : "regular"} /> {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === "discovery" && <MatchDiscovery profile={me.profile} onChanged={refetch} />}
      {tab === "likes" && <MatchLikesYou onGoPlans={() => setTab("plans")} />}
      {tab === "matches" && <MatchMatches />}
      {tab === "plans" && <MatchPlans />}
      {tab === "profile" && <MatchProfile profile={me.profile} onChanged={refetch} />}
    </div>
  );
}
